import { useState, useImperativeHandle, useCallback } from 'react';
import { IShareInfo, IUseCaetShareParams, IUseCaetShareReturn, IconClickType } from '../interface/share';

import { copyLinkToClipboard, getShareIconList, generateShareLink } from '../dataFlow';
import { CertificatePermission } from '../interface/api';
/**
 * CaetShare 组件逻辑层 Hook
 */
export const useCaetShare = (params: IUseCaetShareParams): IUseCaetShareReturn => {
    const {
        link,
        showShareIcon,
        iconList,
        onPermission,
        ref,
        multiple = false,
        permission = CertificatePermission.PUBLIC,
        iconClickType = IconClickType.JUMP,
    } = params;
    // 状态管理
    const [info, setInfo] = useState<IShareInfo>({
        permission,
        selectedIcons: [],
    });

    // 使用默认的图标列表
    const displayIconList = iconList || getShareIconList();

    // 暴露给父组件的方法
    useImperativeHandle(ref, () => ({
        getShareInfo: () => info,
    }));

    // 权限开关切换处理
    const handlePermissionChange = useCallback(
        (checked: boolean): void => {
            const newPermission = checked ? CertificatePermission.PUBLIC : CertificatePermission.PRIVATE;
            setInfo((prev) => ({ ...prev, permission: newPermission }));
            onPermission?.(newPermission);
        },
        [onPermission],
    );

    // 分享图标点击处理
    const handleIconClick = useCallback(
        (iconKey: string): void => {
            if (iconClickType === IconClickType.JUMP) {
                // 跳转模式：直接跳转到对应链接
                const shareLink = generateShareLink({
                    originalLink: link,
                    multiple,
                    selectedIcons: [iconKey],
                    iconList: displayIconList,
                });
                window.open(shareLink, '_blank');
                return;
            }

            // 选择模式：原有的选择逻辑
            setInfo((prev) => {
                if (multiple) {
                    // 多选模式：可以选中/取消选中多个图标
                    const newSelectedIcons = prev.selectedIcons.includes(iconKey)
                        ? prev.selectedIcons.filter((key) => key !== iconKey)
                        : [...prev.selectedIcons, iconKey];
                    return { ...prev, selectedIcons: newSelectedIcons };
                } else {
                    // 单选模式：只能选中一个图标
                    const newSelectedIcons = prev.selectedIcons.includes(iconKey) ? [] : [iconKey];
                    return { ...prev, selectedIcons: newSelectedIcons };
                }
            });
        },
        [multiple, iconClickType, link, displayIconList],
    );

    // 复制链接到剪贴板
    const handleCopyLink = useCallback((): void => {
        const shareLink = generateShareLink({
            originalLink: link,
            multiple,
            selectedIcons: info.selectedIcons,
            iconList: displayIconList,
        });
        copyLinkToClipboard(shareLink);
    }, [link, multiple, info.selectedIcons, displayIconList]);

    // 判断是否展示图标
    const shouldShowIcons = showShareIcon && displayIconList.length > 0;

    // 当前显示的链接（跳转模式下始终显示原始链接）
    const displayLink =
        iconClickType === IconClickType.JUMP
            ? link
            : generateShareLink({
                  originalLink: link,
                  multiple,
                  selectedIcons: info.selectedIcons,
                  iconList: displayIconList,
              });

    return {
        info,
        displayIconList,
        shouldShowIcons,
        handlePermissionChange,
        handleIconClick,
        handleCopyLink,
        displayLink,
    };
};
